"use client";

import { Button } from "@/components/ui/button";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { useJourneyData } from "@/hooks/use-journey-data";
import { authenticatedFetch } from "@/lib/auth";
import { PRE_APPROVAL_REF } from "@/lib/constants/documents";
import { cn } from "@/lib/utils";
import type { Property } from "@/schemas/property";
import type { ViewingInvite } from "@/schemas/viewing-invite";
import * as Icons from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import { CalSetupModal } from "./schedule/cal-setup-modal";
import { ScheduleATourDialog } from "./schedule/schedule-a-tour-dialog";

interface PropertySheetProps {
  property: Property | null;
  savedPropertyId?: string;
  onClose: () => void;
}

export function PropertySheet({
  property,
  savedPropertyId,
  onClose,
}: PropertySheetProps) {
  const {
    savedProperties,
    updateProperty,
    createProperty,
    removeProperty,
    documents,
    journeyDocuments,
    journeyData,
    mortgageQuotes,
  } = useJourneyData();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [notes, setNotes] = useState("");
  const [hasChanges, setHasChanges] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [isScheduleTourDialogOpen, setIsScheduleTourDialogOpen] =
    useState(false);
  const [isCalSetupOpen, setIsCalSetupOpen] = useState(false);
  const [isScheduling, setIsScheduling] = useState(false);
  const [currentInvite, setCurrentInvite] = useState<ViewingInvite | null>(
    null,
  );
  const { toast } = useToast();

  const savedProperty = savedProperties.find((sp) => sp.id === savedPropertyId);

  // Derive the current saved state from savedProperties
  const isSaved = useMemo(
    () =>
      savedProperties.some(
        (sp) =>
          // Check both savedPropertyId and property URL for optimistic updates
          sp.id === savedPropertyId ||
          (property && sp.property?.url === property.url),
      ),
    [savedProperties, savedPropertyId, property],
  );

  // Reset state when property changes
  useEffect(() => {
    if (savedProperty) {
      setNotes(savedProperty.notes || "");
      setHasChanges(false);
    }
  }, [savedProperty]);

  const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNotes(e.target.value);
    setHasChanges(e.target.value !== savedProperty?.notes);
  };

  const handleSaveNotes = () => {
    if (!savedPropertyId) return;
    updateProperty.mutate({
      savedPropertyId,
      updates: { notes },
    });
  };

  const handleSheetClose = async () => {
    setCurrentImageIndex(0);
    onClose();
  };

  // Image carousel handlers
  const hasMultipleImages = property?.images && property.images.length > 1;

  const nextImage = () => {
    if (!property?.images) return;
    setCurrentImageIndex((prev) =>
      prev === property.images!.length - 1 ? 0 : prev + 1,
    );
  };

  const previousImage = () => {
    if (!property?.images) return;
    setCurrentImageIndex((prev) =>
      prev === 0 ? property.images!.length - 1 : prev - 1,
    );
  };

  const isPropertyManual = useMemo(
    () => property?.source === "manual",
    [property?.source],
  );

  const handleSaveProperty = async () => {
    if (!property?.url) return;
    if (!journeyData?.id) {
      toast({
        title: "No Active Journey",
        description:
          "You need to create or select a journey before saving properties.",
        variant: "destructive",
      });
      return;
    }
    try {
      await createProperty.mutateAsync(property.url);
      handleSheetClose();
    } catch (error) {
      // Error handling is managed by the mutation
      console.error(error);
    }
  };

  const handleUnsaveProperty = async () => {
    if (!savedPropertyId) return;
    try {
      await removeProperty.mutateAsync(savedPropertyId);
      handleSheetClose();
    } catch (error) {
      // Error handling is managed by the mutation
      console.error(error);
    }
  };

  const DESCRIPTION_PREVIEW_LENGTH = 300;

  function truncateDescription(text: string) {
    if (text.length <= DESCRIPTION_PREVIEW_LENGTH) return text;
    return `${text.slice(0, DESCRIPTION_PREVIEW_LENGTH).trim()}...`;
  }

  // Check if user has pre-approval and cal link
  const hasPreApproval = journeyDocuments.some(
    (bd) => bd.reference_id === PRE_APPROVAL_REF,
  );
  const hasCalLink = !!journeyData?.calLink;

  const handleScheduleTour = async () => {
    if (!property?.id) return;

    setIsScheduling(true);
    try {
      const response = await authenticatedFetch("/api/viewing-invites", {
        method: "POST",
        body: JSON.stringify({ propertyId: property.id }),
      });

      if (!response.ok) {
        throw new Error("Failed to create viewing invite");
      }

      const invite = await response.json();
      setCurrentInvite(invite);
      setIsScheduleTourDialogOpen(true);
    } catch (error) {
      toast({
        description: "Failed to create viewing invite. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsScheduling(false);
    }
  };

  if (!property) return null;

  return (
    <Sheet open={!!property} onOpenChange={handleSheetClose}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl overflow-y-auto p-0"
      >
        {/* Image Carousel */}
        <div className="relative h-72">
          {property.images?.[currentImageIndex] ? (
            <Image
              src={property.images[currentImageIndex]}
              alt={`Property image ${currentImageIndex + 1}`}
              fill
              className="object-cover"
            />
          ) : (
            <div className="w-full h-full bg-black/10 flex items-center justify-center">
              <Icons.Home className="h-8 w-8 text-black/40" />
            </div>
          )}
          {hasMultipleImages && (
            <>
              <Button
                variant="ghost"
                size="icon"
                onClick={previousImage}
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/20 hover:bg-black/40 text-white rounded-full"
              >
                <Icons.ChevronLeft className="h-5 w-5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={nextImage}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/20 hover:bg-black/40 text-white rounded-full"
              >
                <Icons.ChevronRight className="h-5 w-5" />
              </Button>
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/20 px-2 py-1 rounded-full text-xs text-white">
                {currentImageIndex + 1} / {property.images?.length ?? 0}
              </div>
            </>
          )}
        </div>

        <div className="p-6 space-y-6">
          {/* Source Info */}
          <div className="flex items-center justify-between">
            {!isPropertyManual && (
              <div className="flex items-center gap-2">
                <div className="bg-white rounded-lg p-1.5 shadow-sm border border-gray-100">
                  <img
                    src={`https://logo.clearbit.com/${property.source}`}
                    alt={property.source}
                    className="w-5 h-5 object-contain"
                  />
                </div>
                <span className="text-sm text-gray-600">
                  Listed on{" "}
                  <span className="font-semibold">{property.source}</span>
                </span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="default"
                  onClick={() => {
                    if (isSaved) {
                      handleUnsaveProperty();
                    } else {
                      handleSaveProperty();
                    }
                  }}
                  disabled={
                    createProperty.isPending || removeProperty.isPending
                  }
                >
                  <Icons.Heart
                    className={cn(
                      "mr-2 h-4 w-4 transition-colors",
                      isSaved
                        ? "fill-red-500 stroke-red-500"
                        : "stroke-gray-600",
                    )}
                  />
                  {isSaved ? "Saved" : "Save"}
                </Button>
                {!isPropertyManual && (
                  <Button
                    variant="ghost"
                    size="default"
                    onClick={() => window.open(property.url, "_blank")}
                    className="text-gray-900 hover:bg-canary transition-colors"
                  >
                    View Listing
                    <Icons.ExternalLink className="ml-1 h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Property Info */}
          <div className="space-y-4">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">
                {property.address}
              </h3>
              <p className="text-sm text-gray-600">
                {property.city}, {property.state} {property.zip}
              </p>
              <div className="text-3xl font-semibold text-gray-900 mt-2">
                ${property.price.toLocaleString()}
              </div>
            </div>

            {/* Property Details Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-gray-500">Bedrooms</div>
                <div className="font-semibold">
                  {property.bedrooms ?? "N/A"}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Bathrooms</div>
                <div className="font-semibold">
                  {property.bathrooms ?? "N/A"}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Square Feet</div>
                <div className="font-semibold">
                  {property.square_feet?.toLocaleString() ?? "N/A"}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Year Built</div>
                <div className="font-semibold">
                  {property.year_built ?? "N/A"}
                </div>
              </div>
            </div>

            {property.description && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">
                  Description
                </h4>
                <div className="space-y-2">
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">
                    {isDescriptionExpanded
                      ? property.description
                      : truncateDescription(property.description)}
                  </p>
                  {property.description.length > DESCRIPTION_PREVIEW_LENGTH && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        setIsDescriptionExpanded(!isDescriptionExpanded)
                      }
                      className="text-blue-500 hover:text-blue-600 p-0 h-auto font-normal hover:bg-transparent"
                    >
                      {isDescriptionExpanded ? "Show less" : "Show more"}
                      <Icons.ChevronDown
                        className={cn(
                          "ml-1 h-4 w-4 transition-transform",
                          isDescriptionExpanded && "rotate-180",
                        )}
                      />
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Add Schedule Tour Button */}
          {isSaved && (
            <div className="flex flex-col gap-2">
              <Button
                variant="default"
                size="lg"
                className={cn(
                  "w-full gap-2 bg-canary hover:bg-canary/90 text-gray-900",
                  (!hasPreApproval || !hasCalLink) &&
                    "opacity-50 cursor-not-allowed hover:bg-canary disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-canary",
                )}
                onClick={handleScheduleTour}
                disabled={!hasPreApproval || !hasCalLink || isScheduling}
              >
                {isScheduling ? (
                  <>
                    <Icons.Loader2 className="h-4 w-4 animate-spin" />
                    Creating Invite...
                  </>
                ) : (
                  <>
                    <Icons.Calendar className="h-4 w-4" />
                    Schedule a Tour
                  </>
                )}
              </Button>

              {(!hasPreApproval || !hasCalLink) && (
                <p className="text-sm text-center text-gray-600">
                  {!hasPreApproval && !hasCalLink ? (
                    <>
                      First,{" "}
                      <Link
                        href="/app/planning/mortgage-pre-approval"
                        className="text-blue-600 hover:underline"
                      >
                        upload a pre-approval letter
                      </Link>{" "}
                      and{" "}
                      <button
                        onClick={() => setIsCalSetupOpen(true)}
                        className="text-blue-600 hover:underline"
                        type="button"
                      >
                        set up your calendar
                      </button>
                    </>
                  ) : !hasPreApproval ? (
                    <>
                      First,{" "}
                      <Link
                        href="/app/planning/mortgage-pre-approval"
                        className="text-blue-600 hover:underline"
                      >
                        upload a pre-approval letter
                      </Link>
                    </>
                  ) : (
                    <>
                      First,{" "}
                      <button
                        onClick={() => setIsCalSetupOpen(true)}
                        className="text-blue-600 hover:underline"
                        type="button"
                      >
                        set up your calendar
                      </button>
                    </>
                  )}
                </p>
              )}
            </div>
          )}

          {/* Notes Section */}
          {savedPropertyId && (
            <div className="space-y-4">
              <h4 className="font-semibold text-gray-900">Notes</h4>

              <Textarea
                placeholder="Add private notes about this property..."
                value={notes}
                onChange={handleNotesChange}
                className="min-h-[120px] resize-none border-gray-200 focus:border-gray-300 focus:ring-gray-300"
                disabled={updateProperty.isPending}
              />

              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500">
                  Notes are private and only visible to you
                </p>

                <Button
                  size="sm"
                  onClick={handleSaveNotes}
                  disabled={!hasChanges || updateProperty.isPending}
                  className={cn(
                    "transition-all",
                    hasChanges
                      ? "opacity-100 bg-black hover:bg-black/90 text-white"
                      : "opacity-0 bg-black/70 text-white/70",
                  )}
                >
                  {updateProperty.isPending ? (
                    <>
                      <Icons.Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Icons.Pencil className="mr-2 h-4 w-4" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      </SheetContent>

      <CalSetupModal open={isCalSetupOpen} onOpenChange={setIsCalSetupOpen} />

      <ScheduleATourDialog
        property={property}
        invite={currentInvite}
        open={isScheduleTourDialogOpen}
        onOpenChange={setIsScheduleTourDialogOpen}
        onClose={() => {
          setIsScheduleTourDialogOpen(false);
          setCurrentInvite(null);
        }}
      />
    </Sheet>
  );
}
